import {
  useEntityFilesInfinite,
  useEntityFilesSuspense,
  useDeleteEntityFile,
  type EntityFilesProps,
  type EntityFilesResponse
} from "@swivu/client-api";
import {
  DocumentArrowDownIcon,
  PlusIcon
} from "@heroicons/react/24/outline/index.js";
import CommonStyles from "./CommonStyles.js";
import { useDeferredValue, useState } from "react";
import {
  FileCard,
  FileCardSkeleton,
  Button,
  cn,
  useDebounce,
  useInfiniteScroll,
  useIsGummy,
  type FileData
} from "@swivu/shared-ui";
import { useQueryClient } from "@tanstack/react-query";

type Props = EntityFilesProps & {
  boardId?: number;
  title: string;
  canAdd?: boolean;
  onAdd?: () => void;
  onAnalyze?: (file: FileData) => void;
  advisorsEnabled?: boolean;
};

const EntityFiles = (props: Props) => {
  if (props.api === "wf" && props.boardId) {
    return <PaginatedEntityFiles {...props} boardId={props.boardId} />;
  }

  return <LegacyEntityFiles {...props} />;
};

const LegacyEntityFiles = ({
  title,
  canAdd,
  onAdd,
  onAnalyze,
  advisorsEnabled,
  boardId: _boardId,
  ...variables
}: Props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const queryClient = useQueryClient();
  const { data } = useEntityFilesSuspense(variables.entity_type)({
    variables
  });

  const deleteMutation = useDeleteEntityFile({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [`entity${variables.entity_type}FilesWithSuspense`]
      });
      // Invalidate detail query so files_count updates
      if (variables.entity_type === "task") {
        void queryClient.invalidateQueries({ queryKey: ["taskDetail"] });
      } else if (variables.entity_type === "resource") {
        void queryClient.invalidateQueries({ queryKey: ["resourceDetail"] });
      }
    }
  });

  const handleDelete = (id: number | string) => {
    deleteMutation.mutate({ id: Number(id) });
  };

  return (
    <Files
      files={data}
      title={title}
      canAdd={canAdd}
      onAdd={onAdd}
      onDelete={handleDelete}
      onAnalyze={onAnalyze}
      advisorsEnabled={advisorsEnabled}
      searchTerm={searchTerm}
      onSearch={setSearchTerm}
      filterLocally
      deletingId={
        deleteMutation.isPending ? deleteMutation.variables?.id : undefined
      }
    />
  );
};

type PaginatedEntityFilesProps = Omit<Props, "api" | "boardId"> & {
  api: "wf";
  boardId: number;
};

const PaginatedEntityFiles = ({
  title,
  canAdd,
  onAdd,
  onAnalyze,
  advisorsEnabled,
  boardId,
  id,
  entity_type
}: PaginatedEntityFilesProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm.trim(), 300);
  const deferredSearch = useDeferredValue(debouncedSearch);
  const queryClient = useQueryClient();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useEntityFilesInfinite({
      variables: {
        id,
        board_id: boardId,
        entity_type,
        ...(deferredSearch ? { search: deferredSearch } : {})
      }
    });
  const files = data.pages.flatMap((page) => page.results);
  const totalCount = data.pages[0]?.count ?? 0;
  const { lastElementRef } = useInfiniteScroll({
    isLoading: isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  });

  const deleteMutation = useDeleteEntityFile({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [`entity${entity_type}FilesWithSuspense`]
      });
      if (entity_type === "task") {
        void queryClient.invalidateQueries({ queryKey: ["taskDetail"] });
      } else if (entity_type === "resource") {
        void queryClient.invalidateQueries({ queryKey: ["resourceDetail"] });
      }
    }
  });

  return (
    <Files
      files={files}
      totalCount={totalCount}
      title={title}
      canAdd={canAdd}
      onAdd={onAdd}
      onDelete={(fileId) => deleteMutation.mutate({ id: Number(fileId) })}
      onAnalyze={onAnalyze}
      advisorsEnabled={advisorsEnabled}
      searchTerm={searchTerm}
      onSearch={setSearchTerm}
      loadMoreRef={lastElementRef}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      deletingId={
        deleteMutation.isPending ? deleteMutation.variables?.id : undefined
      }
    />
  );
};

type Files = EntityFilesResponse;

type FilesProps = {
  files: Files;
  totalCount?: number;
  title: string;
  canAdd?: boolean;
  onAdd?: () => void;
  onDelete?: (id: number | string) => void;
  onAnalyze?: (file: FileData) => void;
  advisorsEnabled?: boolean;
  deletingId?: number;
  searchTerm: string;
  onSearch: (search: string) => void;
  filterLocally?: boolean;
  loadMoreRef?: (node: HTMLElement | null) => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
};

const Files = ({
  files,
  totalCount = files.length,
  title,
  canAdd,
  onAdd,
  onDelete,
  onAnalyze,
  advisorsEnabled,
  deletingId,
  searchTerm,
  onSearch,
  filterLocally = false,
  loadMoreRef,
  hasNextPage,
  isFetchingNextPage
}: FilesProps) => {
  const isGummy = useIsGummy();

  if (files.length === 0 && !searchTerm) return null;

  const filteredFiles = filterLocally
    ? files.filter((file) => {
        const searchLower = searchTerm.toLowerCase();
        if (!searchTerm) return true;
        return (
          file.name?.toLowerCase().includes(searchLower) ||
          file.description?.toLowerCase().includes(searchLower)
        );
      })
    : files;

  const addButton =
    canAdd && onAdd ? (
      isGummy ? (
        <button
          type="button"
          onClick={onAdd}
          aria-label="Add media"
          className={cn(
            "mobile-pref-control-tight inline-flex h-14 w-14 items-center justify-center rounded-surface-sm",
            "shadow-raised",
            "bg-tile-media text-white",
            "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
            "motion-reduce:transform-none motion-reduce:transition-none",
            "outline-none focus-visible:ring-4 focus-visible:ring-white/80"
          )}
          style={{ textShadow: "var(--text-shadow-on-color-strong)" }}
        >
          <PlusIcon
            className="mobile-pref-control-icon mobile-pref-detail-inline-icon size-7"
            strokeWidth={3}
          />
        </button>
      ) : (
        <Button
          action="secondary"
          onClick={onAdd}
          aria-label="Add media"
          className="mobile-pref-control-tight h-9"
        >
          <PlusIcon className="mobile-pref-control-icon mobile-pref-detail-inline-icon" />
        </Button>
      )
    ) : null;

  return (
    <CommonStyles
      Icon={DocumentArrowDownIcon}
      tone="files"
      title={`Files / Media`}
      badge={totalCount}
      cardTitle={`${title}`}
      cardSubtitle={`Files`}
      wrapperClassName={`overflow-y-auto`}
      search={{
        filter: searchTerm,
        clearSearch: () => onSearch(""),
        onSearch
      }}
      actions={addButton}
    >
      <div className="flex h-fit w-full flex-col gap-3 pt-4 sm:grid sm:grid-cols-3 sm:items-start">
        {filteredFiles.map((file) => (
          <FileCard
            key={file.id}
            file={file}
            onDelete={onDelete}
            onAnalyze={onAnalyze}
            isDeleting={deletingId === file.id}
            advisorsEnabled={advisorsEnabled}
          />
        ))}
        {isFetchingNextPage &&
          Array.from({ length: 3 }).map((_, index) => (
            <FileCardSkeleton key={`next-page-${index}`} />
          ))}
        {filteredFiles.length === 0 && searchTerm ? (
          <p className="col-span-full py-8 text-center text-sm text-mono-500 dark:text-mono-400">
            No files match your search.
          </p>
        ) : null}
        {hasNextPage ? (
          <div
            ref={loadMoreRef}
            className="col-span-full h-px w-full"
            aria-hidden="true"
          />
        ) : null}
      </div>
    </CommonStyles>
  );
};

export { EntityFiles };
